import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Container, Dimmer, Grid, Icon, Input, Label, Modal, Segment } from 'semantic-ui-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { stockyApi } from '../misc/StockyApi';
import { useAuth } from '../context/AuthContext';
import { useOnboarding } from '../../hooks/useOnboarding';
import images from '../../images';
import AddTrackingScript from '../tracking/AddTrackingScript';

const MARKETPLACE_OPTIONS = [
  {
    type: 'mercado_libre',
    title: 'Mercado Libre',
    image: images.mercadoLibre,
    imageStyle: {
      width: '72%',
      height: '100%',
      objectFit: 'contain',
      borderRadius: '20px',
    },
    accent: '#FFE600',
  },
  {
    type: 'tienda_nube',
    title: 'Tienda Nube',
    image: images.tiendaNube,
    imageStyle: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      borderRadius: '20px',
    },
    accent: '#111827',
  },
];

const baseCardStyle = {
  borderRadius: 18,
  border: '1px solid #E5E7EB',
  background: '#FFFFFF',
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
};

const getStoredCatalogExpectation = () => {
  const value = sessionStorage.getItem('newAccount.catalogWasEmptyAtSelection');
  return value === 'true' || value === 'false' ? value : undefined;
};

function NewAccount() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getUser } = useAuth();
  const currentUser = user || getUser();
  const authUser = useMemo(
    () => (currentUser?.accessToken ? { accessToken: currentUser.accessToken } : null),
    [currentUser?.accessToken],
  );

  const { hasExistingItems, onboardingSafeMode, setOnboardingSafeMode, safeModeResolved, refreshCatalogState } =
    useOnboarding(authUser);

  const [isLoading, setIsLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState({
    itemsProcessed: 0,
    itemsSynced: 0,
    itemErrors: 0,
    status: '',
  });
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [mlSyncTokenId, setMlSyncTokenId] = useState('');
  const [mlSeller, setMlSeller] = useState(null);
  const [mlProducts, setMlProducts] = useState([]);
  const [selectedMlIds, setSelectedMlIds] = useState([]);
  const [mlFilter, setMlFilter] = useState('');
  const [activeMarketplace, setActiveMarketplace] = useState('');
  const [selectedMarketplace, setSelectedMarketplace] = useState('');
  const [importFlowStep, setImportFlowStep] = useState(1);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState('');
  const handledOAuthCallbackRef = useRef('');

  const resolveUserOrRedirect = () => {
    if (!authUser) {
      navigate('/login');
      throw new Error('No authenticated user available');
    }
    return authUser;
  };

  useEffect(() => {
    if (!safeModeResolved) return;
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const queryAccountType = params.get('accountType');

    if (code && queryAccountType) {
      const callbackKey = `${queryAccountType}:${code}`;
      if (handledOAuthCallbackRef.current === callbackKey) return;
      handledOAuthCallbackRef.current = callbackKey;
      void handleAccountCode(code, queryAccountType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, safeModeResolved]);

  const syncModeSummary = useMemo(() => {
    if (!hasExistingItems) {
      return {
        label: 'Primera importacion',
        tone: 'blue',
        title: 'Se importan todas las publicaciones conectadas',
        description:
          'Como todavia no tenes publicaciones en Stocky, vamos a traer todas las publicaciones de la cuenta y usar el stock del canal como base inicial.',
      };
    }

    if (onboardingSafeMode) {
      return {
        label: 'Importacion cuidada',
        tone: 'green',
        title: 'Importa novedades y cuida tu stock actual',
        description:
          'Siempre agrega publicaciones nuevas y actualiza precio y estado de las existentes. Para los SKU ya cargados, mantiene el stock que ya tenes en Stocky.',
      };
    }

    return {
      label: 'Importacion completa',
      tone: 'red',
      title: 'Importa novedades y relee stock desde el canal',
      description:
        'Tambien agrega publicaciones nuevas y actualiza precio y estado, pero reemplaza el stock de los SKU ya existentes con el valor que venga del canal.',
    };
  }, [hasExistingItems, onboardingSafeMode]);

  const syncingDescription = useMemo(() => {
    if (!hasExistingItems) {
      return 'Estamos trayendo las publicaciones de la cuenta y validando de forma segura el stock que se usará como base inicial en Stocky.';
    }
    if (onboardingSafeMode) {
      return 'Estamos importando publicaciones nuevas y actualizando precio y estado sin tocar el stock actual de tus SKU existentes.';
    }
    return 'Estamos importando publicaciones nuevas, actualizando precio y estado, y refrescando el stock de los SKU existentes desde el canal.';
  }, [hasExistingItems, onboardingSafeMode]);

  const pilotSelectionSummary = useMemo(() => {
    if (!mlProducts.length) {
      return 'No encontramos publicaciones disponibles para importar.';
    }

    if (selectedMlIds.length === mlProducts.length) {
      return 'Se van a importar todas las publicaciones detectadas.';
    }

    if (!selectedMlIds.length) {
      return 'No hay publicaciones seleccionadas.';
    }

    return `Se van a importar ${selectedMlIds.length} de ${mlProducts.length} publicaciones.`;
  }, [mlProducts.length, selectedMlIds.length]);

  const filteredProducts = useMemo(() => {
    const term = String(mlFilter || '')
      .trim()
      .toLowerCase();
    if (!term) return mlProducts;
    return mlProducts.filter((product) => {
      const title = String(product.title || '').toLowerCase();
      const variationName = String(product.variationName || '').toLowerCase();
      const sku = String(product.sku || '').toLowerCase();
      const marketplaceId = String(product.marketplaceId || '').toLowerCase();
      return title.includes(term) || variationName.includes(term) || sku.includes(term) || marketplaceId.includes(term);
    });
  }, [mlProducts, mlFilter]);

  const pollSyncStatus = async (syncId, user) =>
    new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const res = await stockyApi.getAccountSyncStatus(syncId, user);
          const data = res.data || {};

          setSyncProgress({
            itemsProcessed: Number(data.itemsProcessed || 0),
            itemsSynced: Number(data.itemsSynced || 0),
            itemErrors: Number(data.itemErrors || 0),
            status: String(data.status || ''),
          });

          if (data.status === 'COMPLETED') {
            clearInterval(interval);
            resolve(data);
          }

          if (data.status === 'FAILED') {
            clearInterval(interval);
            reject(new Error(data.error || 'Error importando cuenta'));
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 900);
    });

  const runAsyncSyncAndWait = async (promiseFactory) => {
    const user = resolveUserOrRedirect();
    const response = await promiseFactory(user);
    if (response.data?.started && response.data?.syncId) {
      const finalData = await pollSyncStatus(response.data.syncId, user);
      sessionStorage.removeItem('newAccount.onboardingSafeMode');
      sessionStorage.removeItem('newAccount.catalogWasEmptyAtSelection');
      await refreshCatalogState({ ignoreStoredPreference: true });
      setSyncResult(finalData);
      return finalData;
    }
    return null;
  };

  const resetSyncUi = () => {
    setSyncError('');
    setSyncResult(null);
    setSyncProgress({
      itemsProcessed: 0,
      itemsSynced: 0,
      itemErrors: 0,
      status: '',
    });
  };

  const chooseMarketplace = (marketplace) => {
    setSelectedMarketplace(marketplace);
    setActiveMarketplace(marketplace);
    setImportFlowStep(2);
  };

  const goToChannelStep = () => {
    setImportFlowStep(1);
    setSyncError('');
  };

  const runLegacySync = async (code, accountType) => {
    const catalogWasEmptyAtSelection = getStoredCatalogExpectation();
    await runAsyncSyncAndWait((user) =>
      stockyApi.syncAccount(code, accountType, user, {
        async: 'true',
        onboardingSafeMode: onboardingSafeMode ? 'true' : 'false',
        catalogWasEmptyAtSelection,
      }),
    );
  };

  const handleAccountCode = async (code, accountType) => {
    try {
      resetSyncUi();
      setActiveMarketplace(accountType);
      setSelectedMarketplace(accountType);
      setImportFlowStep(3);
      setIsLoading(true);
      setSyncProgress({
        itemsProcessed: 0,
        itemsSynced: 0,
        itemErrors: 0,
        status: 'RUNNING',
      });
      const user = resolveUserOrRedirect();

      if (accountType !== 'mercado_libre') {
        await runLegacySync(code, accountType);
        return;
      }

      const controlsRes = await stockyApi.getSyncControlsMe(user);
      const pilotSyncModeEnabled = controlsRes?.data?.pilotSyncModeEnabled === true;
      if (!pilotSyncModeEnabled) {
        await runLegacySync(code, accountType);
        return;
      }

      const previewRes = await stockyApi.previewMercadoLibreSyncSelection(code, user);
      const products = Array.isArray(previewRes?.data?.products) ? previewRes.data.products : [];
      setMlSyncTokenId(String(previewRes?.data?.tokenId || ''));
      setMlSeller(previewRes?.data?.seller || null);
      setMlProducts(products);
      setSelectedMlIds(products.map((product) => String(product.marketplaceId || '')).filter(Boolean));
      setSelectionModalOpen(true);
      setSyncProgress({
        itemsProcessed: 0,
        itemsSynced: 0,
        itemErrors: 0,
        status: '',
      });
    } catch (error) {
      console.error('Failed to synchronize the account', error);
      setSyncError('No se pudo completar la importacion. Revisa la conexion y volve a intentarlo.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelection = (marketplaceId) => {
    setSelectedMlIds((prev) =>
      prev.includes(marketplaceId) ? prev.filter((id) => id !== marketplaceId) : [...prev, marketplaceId],
    );
  };

  const selectAllFiltered = () => {
    const ids = filteredProducts.map((product) => String(product.marketplaceId));
    setSelectedMlIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const clearAllFiltered = () => {
    const ids = new Set(filteredProducts.map((product) => String(product.marketplaceId)));
    setSelectedMlIds((prev) => prev.filter((id) => !ids.has(id)));
  };

  const executeMlPilotSync = async () => {
    if (!mlSyncTokenId) {
      setSyncError('No se pudo resolver la cuenta para importar.');
      return;
    }

    if (!selectedMlIds.length) {
      setSyncError('Seleccioná al menos una publicación para continuar.');
      return;
    }

    try {
      const catalogExpectation = getStoredCatalogExpectation();
      setSelectionModalOpen(false);
      resetSyncUi();
      setActiveMarketplace('mercado_libre');
      setIsLoading(true);
      setSyncProgress({
        itemsProcessed: 0,
        itemsSynced: 0,
        itemErrors: 0,
        status: 'RUNNING',
      });
      await runAsyncSyncAndWait((user) =>
        stockyApi.executeAccountSync(
          {
            tokenId: mlSyncTokenId,
            async: true,
            selectedMlMarketplaceIds: selectedMlIds,
            onboardingSafeMode,
            catalogWasEmptyAtSelection:
              catalogExpectation === undefined ? undefined : catalogExpectation === 'true',
          },
          user,
        ),
      );
    } catch (error) {
      console.error('Failed to execute ML pilot sync', error);
      setSyncError('No se pudo completar la importación seleccionada.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = async (type) => {
    try {
      resetSyncUi();
      setActiveMarketplace(type);
      setIsLoading(true);
      const user = resolveUserOrRedirect();
      sessionStorage.setItem('newAccount.onboardingSafeMode', onboardingSafeMode ? 'true' : 'false');
      sessionStorage.setItem('newAccount.catalogWasEmptyAtSelection', hasExistingItems ? 'false' : 'true');
      const response = await stockyApi.getNewAccountUrl(type, user);
      if (response.status === 200) {
        window.location.href = response.data.url;
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to get the redirect URL', error);
      setSyncError('No se pudo iniciar la conexion con el canal seleccionado.');
      setIsLoading(false);
    }
  };

  const syncResultMetrics = syncResult?.result?.sync || {};
  const syncStockPolicy = syncResultMetrics?.stock || null;
  const stockOutcome = syncStockPolicy
    ? syncStockPolicy.protectedFromStaleEmptyCatalog
      ? {
          icon: 'shield',
          color: '#92400E',
          background: '#FFFBEB',
          border: '#FDE68A',
          title: 'Stock existente protegido',
          description:
            'Mientras conectabas la cuenta detectamos que el catálogo ya tenía publicaciones. Para evitar sobrescribirlas, Stocky conservó su stock actual.',
        }
      : syncStockPolicy.appliedMode === 'preserve_existing_stock'
        ? {
            icon: 'check circle',
            color: '#166534',
            background: '#F0FDF4',
            border: '#BBF7D0',
            title: 'Stock existente conservado',
            description: 'Las publicaciones existentes mantuvieron el stock que ya tenían en Stocky.',
          }
        : {
            icon: 'download',
            color: '#1D4ED8',
            background: '#EFF6FF',
            border: '#BFDBFE',
            title: 'Stock tomado del canal',
            description: 'El stock informado por el canal quedó aplicado como referencia en Stocky.',
          }
    : null;
  const currentMarketplaceLabel =
    MARKETPLACE_OPTIONS.find((option) => option.type === activeMarketplace)?.title || 'la cuenta';
  const selectedMarketplaceOption = MARKETPLACE_OPTIONS.find((option) => option.type === selectedMarketplace) || null;
  const callbackParams = new URLSearchParams(location.search);
  const callbackAccountType = callbackParams.get('accountType');
  const returnPath = location.state?.from === '/inicio' ? '/inicio' : '/canales';

  const retryImport = () => {
    if (mlSyncTokenId && activeMarketplace === 'mercado_libre' && selectedMlIds.length) {
      return executeMlPilotSync();
    }

    const marketplaceToReconnect = callbackAccountType || selectedMarketplace || activeMarketplace;
    if (marketplaceToReconnect) {
      return handleImageClick(marketplaceToReconnect);
    }

    return undefined;
  };

  const returnToImportConfiguration = () => {
    setSelectionModalOpen(false);
    setImportFlowStep(2);
  };

  return (
    <Container style={{ maxWidth: 1180 }}>
      <AddTrackingScript />

      {isLoading && (
        <Dimmer
          active
          page
          style={{
            background: 'rgba(248, 250, 252, 0.88)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            role="status"
            aria-live="polite"
            style={{
              width: 'min(720px, calc(100vw - 40px))',
              padding: 32,
              borderRadius: 24,
              background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
              boxShadow: '0 30px 70px rgba(15, 23, 42, 0.15)',
              color: '#111827',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 999,
                background: '#F3E8FF',
                color: '#7E22CE',
                fontWeight: 700,
                marginBottom: 16,
              }}
            >
              <Icon name="sync alternate" loading />
              Importación en curso
            </div>

            <div style={{ fontSize: 30, fontWeight: 800, marginBottom: 10 }}>Importando {currentMarketplaceLabel}</div>
            <div
              style={{
                fontSize: 16,
                lineHeight: 1.5,
                color: '#4B5563',
                marginBottom: 24,
              }}
            >
              {syncingDescription}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12,
                marginBottom: 20,
              }}
            >
              <div style={{ ...baseCardStyle, padding: 18 }}>
                <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>Procesados</div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{syncProgress.itemsProcessed || 0}</div>
              </div>
              <div style={{ ...baseCardStyle, padding: 18 }}>
                <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>Importados</div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{syncProgress.itemsSynced || 0}</div>
              </div>
              <div style={{ ...baseCardStyle, padding: 18 }}>
                <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>Con error</div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{syncProgress.itemErrors || 0}</div>
              </div>
            </div>

            <div style={{ color: '#6B7280', fontSize: 14 }}>
              Podés dejar esta pantalla abierta. Al finalizar te vamos a mostrar el resultado y los próximos pasos.
            </div>
          </div>
        </Dimmer>
      )}

      <Segment
        style={{
          borderRadius: 26,
          padding: 0,
          overflow: 'hidden',
          boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
          border: '1px solid #E5E7EB',
        }}
      >
        <div
          style={{
            padding: '22px 28px',
            background: 'linear-gradient(135deg, #7E22CE 0%, #C026D3 100%)',
            color: '#FFFFFF',
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Conectar e importar una cuenta</div>
          <div
            style={{
              fontSize: 15,
              lineHeight: 1.5,
              maxWidth: 860,
              opacity: 0.95,
            }}
          >
            Elegí el canal, confirmá cómo querés tratar el stock y seguí la importación hasta ver el resultado.
          </div>
        </div>
        <div style={{ padding: 28, background: '#F8FAFC' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
              marginBottom: 24,
            }}
          >
            {[
              {
                step: '1',
                title: 'Canal',
                description: 'Elegís la cuenta que querés conectar o volver a importar.',
              },
              {
                step: '2',
                title: 'Configuración',
                description: 'Revisás el alcance y confirmás qué pasa con el stock.',
              },
              {
                step: '3',
                title: 'Importación',
                description: 'Ves el progreso, el resultado y la siguiente acción.',
              },
            ].map((step) => (
              <div
                key={step.step}
                style={{
                  ...baseCardStyle,
                  padding: 18,
                  background: '#FFFFFF',
                  borderColor:
                    Number(step.step) === importFlowStep || (syncResult && step.step === '3') ? '#A855F7' : '#E5E7EB',
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 12,
                    background: '#F3E8FF',
                    color: '#7E22CE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    marginBottom: 12,
                  }}
                >
                  {step.step}
                </div>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>{step.title}</div>
                <div style={{ color: '#6B7280', fontSize: 14, lineHeight: 1.45 }}>{step.description}</div>
              </div>
            ))}
          </div>

          {syncResult ? (
            <div
              style={{
                ...baseCardStyle,
                padding: 28,
                marginBottom: 24,
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F0FDF4 100%)',
                borderColor: '#BBF7D0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    width: 58,
                    height: 58,
                    borderRadius: 18,
                    background: '#DCFCE7',
                    color: '#15803D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon name="check circle" size="large" />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 12px',
                      borderRadius: 999,
                      background: '#DCFCE7',
                      color: '#166534',
                      fontWeight: 700,
                      marginBottom: 12,
                    }}
                  >
                    <Icon name="thumbs up" />
                    Importación completada
                  </div>

                  <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 10 }}>La cuenta quedó lista</div>
                  <div
                    style={{
                      color: '#4B5563',
                      fontSize: 16,
                      lineHeight: 1.5,
                      marginBottom: 18,
                    }}
                  >
                    Importamos las publicaciones seleccionadas y actualizamos la información disponible de la cuenta.
                  </div>

                  {stockOutcome ? (
                    <div
                      role="status"
                      style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start',
                        padding: 16,
                        borderRadius: 14,
                        marginBottom: 18,
                        color: stockOutcome.color,
                        background: stockOutcome.background,
                        border: `1px solid ${stockOutcome.border}`,
                      }}
                    >
                      <Icon name={stockOutcome.icon} aria-hidden="true" />
                      <div>
                        <div style={{ fontWeight: 800, marginBottom: 4 }}>{stockOutcome.title}</div>
                        <div style={{ lineHeight: 1.5 }}>{stockOutcome.description}</div>
                      </div>
                    </div>
                  ) : null}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                      gap: 12,
                      marginBottom: 18,
                    }}
                  >
                    <div style={{ ...baseCardStyle, padding: 18 }}>
                      <div
                        style={{
                          color: '#6B7280',
                          fontSize: 13,
                          marginBottom: 6,
                        }}
                      >
                        Publicaciones importadas
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 800 }}>
                        {Number(syncResultMetrics?.items?.synced || syncProgress.itemsSynced || 0)}
                      </div>
                    </div>
                    <div style={{ ...baseCardStyle, padding: 18 }}>
                      <div
                        style={{
                          color: '#6B7280',
                          fontSize: 13,
                          marginBottom: 6,
                        }}
                      >
                        Con error
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 800 }}>
                        {Number(syncResultMetrics?.items?.errors || syncProgress.itemErrors || 0)}
                      </div>
                    </div>
                    <div style={{ ...baseCardStyle, padding: 18 }}>
                      <div
                        style={{
                          color: '#6B7280',
                          fontSize: 13,
                          marginBottom: 6,
                        }}
                      >
                        {syncResultMetrics?.orders?.skipped ? 'Órdenes nuevas' : 'Órdenes sincronizadas'}
                      </div>
                      {syncResultMetrics?.orders?.skipped ? (
                        <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.35 }}>
                          Se reciben automáticamente
                        </div>
                      ) : (
                        <div style={{ fontSize: 28, fontWeight: 800 }}>
                          {Number(syncResultMetrics?.orders?.synced || 0)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <Button color="green" onClick={() => navigate('/publicaciones')}>
                      Ver publicaciones
                    </Button>
                    <Button basic onClick={() => navigate('/canales')}>
                      Volver a canales
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {syncError ? (
            <div
              role="alert"
              style={{
                ...baseCardStyle,
                padding: 20,
                marginBottom: 24,
                background: '#FEF2F2',
                borderColor: '#FECACA',
                color: '#991B1B',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <Icon name="warning circle" />
                <strong>No pudimos completar la importacion</strong>
              </div>
              <div style={{ lineHeight: 1.5 }}>{syncError}</div>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginTop: 14,
                }}
              >
                {(mlSyncTokenId && selectedMlIds.length) || callbackAccountType || selectedMarketplace ? (
                  <Button color="red" basic onClick={retryImport}>
                    {mlSyncTokenId && activeMarketplace === 'mercado_libre'
                      ? 'Reintentar importación'
                      : 'Volver a conectar'}
                  </Button>
                ) : null}
                <Button basic onClick={() => navigate(returnPath)}>
                  Cancelar y volver
                </Button>
              </div>
            </div>
          ) : null}

          {importFlowStep === 1 && !syncResult ? (
            <>
              <div
                style={{
                  ...baseCardStyle,
                  padding: 24,
                  marginBottom: 24,
                  background: '#FFFFFF',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginBottom: 12,
                  }}
                >
                  <Label color="purple">Paso 1</Label>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>Elegí el canal que querés importar</div>
                </div>
                <div style={{ color: '#4B5563', lineHeight: 1.6 }}>
                  Vas a continuar en el sitio oficial del marketplace para autorizar la cuenta. Stocky no te va a pedir
                  la contraseña del canal.
                </div>
              </div>

              <Grid stackable columns={2}>
                <Grid.Row stretched>
                  {MARKETPLACE_OPTIONS.map((option) => (
                    <Grid.Column key={option.type}>
                      <div
                        style={{
                          ...baseCardStyle,
                          padding: 22,
                          height: '100%',
                          background: '#FFFFFF',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginBottom: 14,
                          }}
                        >
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: 999,
                              background: option.accent,
                            }}
                          />
                          <div style={{ fontSize: 22, fontWeight: 800 }}>{option.title}</div>
                        </div>
                        <div
                          style={{
                            color: '#4B5563',
                            lineHeight: 1.55,
                            marginBottom: 18,
                          }}
                        >
                          Conectá la cuenta y revisá cómo se van a importar sus publicaciones antes de empezar.
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 150,
                            marginBottom: 18,
                            borderRadius: 22,
                            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                            boxShadow: 'inset 0 0 0 1px rgba(229, 231, 235, 0.9)',
                          }}
                        >
                          <div
                            style={{
                              width: 128,
                              height: 128,
                              borderRadius: 22,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 18px 35px rgba(168, 85, 247, 0.14)',
                              background: '#FFFFFF',
                            }}
                          >
                            <img src={option.image} alt={`Logo de ${option.title}`} style={option.imageStyle} />
                          </div>
                        </div>
                        <Button
                          fluid
                          size="large"
                          color="purple"
                          onClick={() => chooseMarketplace(option.type)}
                          disabled={!safeModeResolved}
                          loading={!safeModeResolved}
                        >
                          Elegir {option.title}
                        </Button>
                      </div>
                    </Grid.Column>
                  ))}
                </Grid.Row>
              </Grid>

              <div style={{ display: 'flex', marginTop: 18 }}>
                <Button basic onClick={() => navigate(returnPath)}>
                  Cancelar y volver
                </Button>
              </div>
            </>
          ) : null}

          {importFlowStep === 2 && selectedMarketplaceOption && !syncResult ? (
            <div style={{ ...baseCardStyle, padding: 24, background: '#FFFFFF' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 14,
                }}
              >
                <Label color="purple">Paso 2</Label>
                <div style={{ fontSize: 24, fontWeight: 800 }}>Revisá la importación</div>
                <Label basic color="violet">
                  <Icon name="plug" /> {selectedMarketplaceOption.title}
                </Label>
              </div>

              <div style={{ color: '#4B5563', lineHeight: 1.6, marginBottom: 18 }}>
                {hasExistingItems
                  ? 'Vamos a sumar publicaciones nuevas y actualizar precio y estado de las existentes. Elegí qué hacer con el stock que Stocky ya tiene cargado.'
                  : 'Esta es tu primera importación. Vamos a traer las publicaciones de la cuenta y usar el stock del canal como base inicial.'}
              </div>

              {hasExistingItems ? (
                <div
                  role="radiogroup"
                  aria-label="Tratamiento del stock existente"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: 14,
                    marginBottom: 20,
                  }}
                >
                  {[
                    {
                      key: 'safe',
                      active: onboardingSafeMode,
                      title: 'Conservar mi stock actual',
                      subtitle: 'Recomendado',
                      description:
                        'Importa novedades y actualiza precio y estado sin reemplazar el stock local de los SKU existentes.',
                      accent: '#15803D',
                      onClick: () => setOnboardingSafeMode(true),
                    },
                    {
                      key: 'full',
                      active: !onboardingSafeMode,
                      title: 'Releer stock del canal',
                      subtitle: 'Reemplaza el stock local',
                      description: 'Usa el stock informado por el canal también para los SKU que ya existen en Stocky.',
                      accent: '#B91C1C',
                      onClick: () => setOnboardingSafeMode(false),
                    },
                  ].map((option) => (
                    <label
                      key={option.key}
                      style={{
                        textAlign: 'left',
                        padding: 18,
                        borderRadius: 18,
                        border: option.active ? `2px solid ${option.accent}` : '1px solid #E5E7EB',
                        background: option.active ? '#FAFAFA' : '#FFFFFF',
                        boxShadow: option.active ? '0 12px 30px rgba(15, 23, 42, 0.08)' : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          marginBottom: 10,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 18 }}>{option.title}</div>
                          <div
                            style={{
                              color: option.accent,
                              fontWeight: 700,
                              marginTop: 4,
                            }}
                          >
                            {option.subtitle}
                          </div>
                        </div>
                        <input
                          type="radio"
                          name="existing-stock-mode"
                          checked={option.active}
                          onChange={option.onClick}
                          style={{ width: 20, height: 20, accentColor: option.accent, cursor: 'pointer' }}
                        />
                      </div>
                      <div style={{ color: '#4B5563', lineHeight: 1.55 }}>{option.description}</div>
                    </label>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: 18,
                    borderRadius: 16,
                    background: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    color: '#1D4ED8',
                    lineHeight: 1.55,
                    marginBottom: 20,
                  }}
                >
                  <Icon name="info circle" />
                  <strong> Primera importación:</strong> el stock del canal será el punto de partida en Stocky.
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button basic onClick={goToChannelStep}>
                    Volver y cambiar canal
                  </Button>
                  <Button basic onClick={() => navigate(returnPath)}>
                    Cancelar
                  </Button>
                </div>
                <Button
                  color="purple"
                  size="large"
                  onClick={() => handleImageClick(selectedMarketplace)}
                  disabled={!safeModeResolved}
                  loading={!safeModeResolved}
                >
                  Conectar e importar {selectedMarketplaceOption.title}
                  <Icon name="external alternate" style={{ marginLeft: 8, marginRight: 0 }} />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Segment>

      <Modal open={selectionModalOpen} onClose={returnToImportConfiguration} size="large" closeOnDimmerClick={false}>
        <Modal.Header>Revisá las publicaciones a importar</Modal.Header>
        <Modal.Content scrolling>
          <div
            style={{
              ...baseCardStyle,
              padding: 18,
              marginBottom: 16,
              background: '#F9FAFB',
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 8 }}>
              {mlSeller?.nickname ? `Cuenta: ${mlSeller.nickname}` : 'Mercado Libre'}
            </div>
            <div style={{ color: '#4B5563', lineHeight: 1.55, marginBottom: 12 }}>
              Esta es la cuenta detectada. Dejamos todas las publicaciones seleccionadas; podés quitar las que no
              quieras importar ahora.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Label color="blue">Detectadas: {mlProducts.length}</Label>
              <Label color={selectedMlIds.length ? 'green' : 'red'}>Seleccionadas: {selectedMlIds.length}</Label>
              <Label>{syncModeSummary.label}</Label>
            </div>
          </div>

          <div style={{ color: '#4B5563', marginBottom: 12 }}>{pilotSelectionSummary}</div>

          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <Input
              placeholder="Filtrar por titulo, SKU o ID..."
              value={mlFilter}
              onChange={(e) => setMlFilter(e.target.value)}
              style={{ flex: 1, minWidth: 260 }}
            />
            <Button basic onClick={selectAllFiltered}>
              Seleccionar visibles
            </Button>
            <Button basic onClick={clearAllFiltered}>
              Limpiar visibles
            </Button>
          </div>

          <div
            style={{
              maxHeight: 420,
              overflow: 'auto',
              border: '1px solid #E5E7EB',
              borderRadius: 10,
            }}
            aria-label="Publicaciones disponibles"
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '24px 2fr 1.2fr 1.1fr 0.9fr 1fr',
                minWidth: 760,
                gap: 10,
                padding: '12px 10px',
                borderBottom: '1px solid #E5E7EB',
                background: '#F9FAFB',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#6B7280',
              }}
            >
              <span />
              <span>Titulo</span>
              <span>Variacion</span>
              <span>SKU</span>
              <span>Estado</span>
              <span>Item ID</span>
            </div>

            {filteredProducts.length ? (
              filteredProducts.map((product) => {
                const marketplaceId = String(product.marketplaceId || '');
                const checked = selectedMlIds.includes(marketplaceId);
                const itemId = marketplaceId.split('-').filter(Boolean)[0] || marketplaceId;
                const status = String(product.status || '').toUpperCase();
                const statusColor = status === 'ACTIVE' ? 'green' : status === 'PAUSED' ? 'yellow' : 'grey';

                return (
                  <div
                    key={marketplaceId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '24px 2fr 1.2fr 1.1fr 0.9fr 1fr',
                      minWidth: 760,
                      gap: 10,
                      padding: '10px 10px',
                      borderBottom: '1px solid #F3F4F6',
                      alignItems: 'center',
                      background: checked ? '#FAFAFA' : '#FFFFFF',
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      aria-label={`Seleccionar ${product.title || marketplaceId}`}
                      onChange={() => toggleSelection(marketplaceId)}
                    />
                    <div
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {product.title || 'Sin titulo'}
                    </div>
                    <div
                      style={{
                        color: '#6B7280',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {product.variationName || '-'}
                    </div>
                    <div
                      style={{
                        color: '#6B7280',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {product.sku || '-'}
                    </div>
                    <div>
                      <Label color={statusColor} basic size="tiny">
                        {status || 'UNKNOWN'}
                      </Label>
                    </div>
                    <div
                      style={{
                        color: '#9CA3AF',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {itemId}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: 28, textAlign: 'center', color: '#6B7280' }}>
                No encontramos publicaciones con ese filtro.
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={returnToImportConfiguration}>Volver a la configuración</Button>
          <Button color="purple" onClick={executeMlPilotSync} disabled={!selectedMlIds.length}>
            Importar {selectedMlIds.length} publicaciones
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
}

export default NewAccount;
